import { MUICheckBox, MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useUsers } from "../../../../../hooks/useUsers"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { FileSharingPost } from "../../../../models/File"
import { UserGet } from "../../../../models/User"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

const validationSchema = z.object({
  userId: z.string().min(1, "User is required"),
  canWrite: z.boolean({ error: "Can Write is required" }),
  canShare: z.boolean({ error: "Can Share is required" }),
})

const returnURL = `/files`

export function FileSharing() {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const { usersList, isLoadingUsers } = useUsers(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
    setValue,
  } = useForm<FileSharingPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      userId: "",
      canWrite: false,
      canShare: false,
    })
  }, [reset])

  const onSubmit = (values: FileSharingPost) => digitalManagerApi
      .post(`/files/${fileId}/sharing`, {
        userId: values.userId,
        canWrite: values.canWrite,
        canShare: values.canShare,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("File shared")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot share the file")
      })

  const submitForm = handleSubmit(onSubmit)

  const dropdownLOV = usersList.map((u: UserGet) => ({
    id: u.id,
    name: u.username ?? "",
  }))

  const selectedUserId = useWatch({ control, name: "userId" })

  const handleUserSelectChange = useCallback(
    (values: Array<string | { id: string }>) => {
      const first = Array.isArray(values) ? values[0] : null
      const id = typeof first === "string" ? first : (first?.id ?? "")
      setValue("userId", id, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const renderUserSelect = useCallback(
    () => (
      <MUISelect
        id="userId"
        name="userId"
        size={32}
        enableSearch
        dropdownLOV={dropdownLOV}
        selectedItem={selectedUserId ? [selectedUserId] : []}
        isDisabled={isLoadingUsers}
        clearable={false}
        onChange={handleUserSelectChange}
        errors={
          typeof errors.userId?.message === "string"
            ? { userId: { message: errors.userId.message } }
            : undefined
        }
      />
    ),
    [dropdownLOV, selectedUserId, errors.userId, isLoadingUsers, handleUserSelectChange]
  )

  const handleCheckboxToggle = useCallback(
    (field: ControllerRenderProps<FileSharingPost, "canWrite" | "canShare">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxCanWrite = useCallback(
    ({ field }: ControllerRendererProps<FileSharingPost, "canWrite">) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  const renderCheckboxCanShare = useCallback(
    ({ field }: ControllerRendererProps<FileSharingPost, "canShare">) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  return (
    <div style={{ marginTop: 16 }}>
      <FormPage
        returnURL={returnURL}
        onSubmit={submitForm}
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        isValid={isValid}
      >
        <FieldRow label="User">
          <Controller name="userId" control={control} render={renderUserSelect} />
        </FieldRow>

        <FieldRow label="Can Write">
          <Controller name="canWrite" control={control} render={renderCheckboxCanWrite} />
        </FieldRow>

        <FieldRow label="Can Share">
          <Controller name="canShare" control={control} render={renderCheckboxCanShare} />
        </FieldRow>
      </FormPage>
    </div>
  )
}
